import { z } from "zod";
import { NOTE_TYPES } from "./templates";
import { NOTE_COLORS } from "./theme";

export const noteColorSchema = z.enum(NOTE_COLORS);
export const noteTypeSchema = z.enum(NOTE_TYPES);

/** A TipTap doc. Structure beyond `type: "doc"` is the editor's business. */
export const richDocSchema = z
  .object({ type: z.literal("doc"), content: z.array(z.record(z.unknown())).optional() })
  .passthrough();

export const createNoteSchema = z.object({
  title: z.string().max(300).optional(),
  content: richDocSchema.optional(),
  color: noteColorSchema.optional(),
  noteType: noteTypeSchema.optional(),
  /** Per-template extras — recipe servings and times today. */
  templateData: z.record(z.unknown()).optional(),
  icon: z.string().max(16).nullish(),
  teamId: z.string().uuid().nullish(),
  isPinned: z.boolean().optional(),
});

export const updateNoteSchema = createNoteSchema.partial().extend({
  isArchived: z.boolean().optional(),
  /**
   * The version the client last read. Required on any content change so a
   * concurrent edit is reported rather than silently overwritten.
   */
  version: z.number().int().positive().optional(),
});

/**
 * Which slice of the library a listing asks for.
 *
 * `personal` is notes the caller owns outside any team, `team` narrows to the
 * `teamId` alongside it, `shared` is notes other people handed over and the
 * caller accepted, and `all` is every note they can reach.
 */
export const NOTE_SCOPES = ["all", "personal", "team", "shared"] as const;
export const noteScopeSchema = z.enum(NOTE_SCOPES);
export type NoteScope = z.infer<typeof noteScopeSchema>;

export const listNotesQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  scope: noteScopeSchema.default("all"),
  teamId: z.string().uuid().optional(),
  tagId: z.string().uuid().optional(),
  noteType: noteTypeSchema.optional(),
  archived: z.enum(["true", "false"]).optional(),
  pinned: z.enum(["true", "false"]).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().datetime().optional(),
});

export type CreateNoteInput = z.infer<typeof createNoteSchema>;
export type UpdateNoteInput = z.infer<typeof updateNoteSchema>;
export type ListNotesQuery = z.infer<typeof listNotesQuerySchema>;

export const createTagSchema = z.object({
  name: z.string().trim().min(1).max(40),
  color: noteColorSchema.optional(),
  teamId: z.string().uuid().nullish(),
});
